import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import {
  FC,
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Breadcrumb from "~/components/Breadcrumb";
import Container from "~/components/Container";
import Layout from "~/layouts/ThemeLayout";

import { get, isEmpty } from "lodash";
import { BreadcrumbJsonLd } from "next-seo";
import dynamic from "next/dynamic";
import React from "react";
import ReactGA4 from "react-ga4";
import { useRecoilValue } from "recoil";
import SEOTags from "~/components/SEOTags";
import {
  Identifiers,
  PATH,
  RelatedPosition,
  SectionsKey,
} from "~/constants/enum";
import {
  FULL_RECENTLY_PRODUCT,
  RECENTLY_PRODUCT,
} from "~/constants/localstore";
import {
  GET_PRODUCTS_TRACKING,
  GET_RELATED_RULES,
  QUERY_GET_META_PRODUCT_DETAIL_BY_SLUG,
  QUERY_PRODUCT_DETAIL_BY_SKU,
  QUERY_RELATED_PRODUCT,
} from "~/data/product/gql";
import { GET_SCHEMA_PRODUCT } from "~/data/schemas/schema.gql";
import useScrollTop from "~/hooks/useScrollTop";
import ProductSchema from "~/schema/ProductSchema";
import RelatedTop from "~/sections/RelatedTop";
import cartAtom from "~/services/cart/cart.atom";
import { ProductCategory, ProductVariant } from "~/services/product";
import ReMaketing, {
  REMARKETING_PRODUCT,
} from "~/services/tracking/ReMaketing";
import { initializeSSRApollo } from "~/utils/apollo-client";
import { striptTags } from "~/utils/converters";
import {
  getPageStruct,
  renderSectionByKey,
  renderSectionNew,
} from "~/utils/pageTwo";
import { getTheme, getUrlCustom, isUsingLayoutV2 } from "~/utils/theme";
import { transformProductItem } from "~/utils/transform";
import { QUERY_GET_MEGA_MENU_GQL } from "~/data/mega-menu/gql";
import {
  GET_STORE_CONFIG_ON_DOCUMENT,
  QUERY_GET_CMS_BLOCKS,
  useRecentlyViewProducts,
} from "~/data/home";
import { useMobile } from "~/hooks/useMobile";
import Icon from "~/components/Icon";

const TrackingSSG = dynamic(() => import("~/components/TrackingSSG"), {
  ssr: false,
});

const pageStruct = getPageStruct(getTheme(), "product-detail-two");

const sections: any = pageStruct?.children
  ?.sort((a: any, b: any) => a.order - b.order)
  .map((item: any) => {
    const Section = renderSectionByKey(item.id);

    return {
      id: item.id,
      order: item.order,
      dataKey: item.dataKey,
      identifier: item.identifier,
      component: Section ?? null,
    };
  });

const SSRIdentifiers = [
  Identifiers.AddYourLogo,
  Identifiers.PdpBanner,
  Identifiers.YouMayAlsoLike,
  Identifiers.BenefitsSection,
  Identifiers.FollowUs,
];

const ProductDetail: FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
  product,
  tracking,
  defaultVariant,
  dataMeta,
  slug,
  b,
  configSchema,
  relatedByPosition,
  firstRelated,
  dataMegaMenu,
  storeConfig,
  relatedTopProducts,
  relatedRules,
  isMobile,
  serverCmsBlocks,
}) => {
  const { refetchGetRelatedProduct } = useRecentlyViewProducts({});
  useScrollTop();

  const { cartId } = useRecoilValue(cartAtom.cartSelector);

  const sectionData = useMemo(() => {
    return {
      [SectionsKey.ProductTags]: {
        sku: product?.sku,
      },
      [SectionsKey.ProductDetailTwo]: {
        product,
        defaultVariant,
        serverCmsBlocks,
        tracking,
      },
      [SectionsKey.Related]: {
        sku: product?.sku,
        parentSlug: slug,
      },
      [SectionsKey.FeaturedProductV2]: {
        data: serverCmsBlocks?.[Identifiers.YouMayAlsoLike] || {},
      },
      [SectionsKey.RecentlyView]: {
        data: undefined,
      },
      [SectionsKey.FollowUs]: {
        data: serverCmsBlocks?.[Identifiers.FollowUs] || {},
      },
    };
  }, [product?.sku, relatedByPosition]);

  const setRecentlyProduct = (SKU: any) => {
    if (typeof window != "undefined") {
      const listRecently = localStorage.getItem(RECENTLY_PRODUCT);
      const listFullRecently = JSON.parse(
        localStorage.getItem(FULL_RECENTLY_PRODUCT) || "[]"
      );
      if (!!listRecently) {
        const parseListRecently = JSON.parse(listRecently) || [];
        if (Array.isArray(parseListRecently)) {
          const index = parseListRecently.findIndex((i) => i === SKU);
          if (index !== -1) {
            parseListRecently.splice(index, 1);
          }
          parseListRecently.unshift(SKU?.toString());
          const listResult = parseListRecently.slice(0, 12);
          localStorage.setItem(RECENTLY_PRODUCT, JSON.stringify(listResult));
          refetchGetRelatedProduct();
        }
        const isDuplicate = listFullRecently?.[0]?.sku === SKU;
        if (!isDuplicate) {
          localStorage.setItem(
            FULL_RECENTLY_PRODUCT,
            JSON.stringify([product, ...(listFullRecently ?? [])])
          );
        }
      } else {
        const tempList = [SKU.toString()];
        localStorage.setItem(RECENTLY_PRODUCT, JSON.stringify(tempList));
      }
    }
  };

  useEffect(() => {
    if (!product?.sku) return;
    setRecentlyProduct(product?.sku);
  }, [product?.sku]);

  const breadcrumbData = useMemo(() => {
    if (isEmpty(product?.categories) || product?.categories?.length === 0)
      return [{ title: "Home", path: "/" }];
    // const categories = product?.categories[product?.categories.length - 1];
    //get maximum level of category
    const maxLevel: any = product?.categories?.reduce(
      (max: number = 0, c: ProductCategory) => {
        return c.level > max ? c.level : max;
      },
      0
    );
    const categories = product?.categories?.find(
      (c: ProductCategory) => c.level === maxLevel
    );

    return [{ title: "Home", path: "/" }].concat(
      categories?.breadcrumbs
        ? categories?.breadcrumbs?.map((c: any) => {
            const path = categories.url_path?.split(c.category_url_key)[0];

            return {
              title: c.category_name,
              path: PATH.CategoryDetail.replace(
                "[slug]",
                path + c.category_url_key
              ),
            };
          }) || []
        : {
            title: categories?.name,
            path: PATH.CategoryDetail.replace("[slug]", categories?.url_path),
          }
    );
  }, []);

  const ProductMeta = useMemo(() => {
    return {
      title: dataMeta?.meta_title || dataMeta?.name,
      description:
        dataMeta?.meta_description ||
        striptTags(dataMeta?.description?.html) ||
        dataMeta?.name,
      keywords: dataMeta?.meta_keyword || dataMeta?.name,
      image: dataMeta?.thumbnail?.url,
      url: getUrlCustom({
        pathname: PATH.ProductDetail,
        query: {
          slug: product?.url_key,
          variant: product?.variants?.[0]?.product?.id,
        },
      }),
      type: "product",
    };
  }, [dataMeta]);

  let linkDefaultVariant = getUrlCustom({
    pathname: process.env.NEXT_PUBLIC_URL + PATH.ProductDetail,
    query: {
      slug: product?.url_key,
      variant: product?.variants?.[0]?.product?.id,
    },
  });

  const offers = product.variants?.map((variant: any) => {
    return {
      "@type": "Offer",
      // link sản phẩm theo variant link/product/url?variant=variantId
      url: getUrlCustom({
        pathname: process.env.NEXT_PUBLIC_URL + PATH.ProductDetail,
        query: {
          slug: product?.url_key,
          variant: variant?.product?.id,
        },
      }),
      price: variant?.product?.price_range?.minimum_price?.final_price?.value,
      priceCurrency:
        variant?.product?.price_range?.minimum_price?.final_price?.currency,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      priceValidUntil:
        variant?.product?.price_range?.minimum_price?.final_price?.value,
    };
  });

  const aggregateRating = !!product.rating_summary
    ? {
        ratingValue: product.rating_summary || (0 as number),
        ratingCount: product.review_count || (0 as number),
      }
    : undefined;

  return (
    <Fragment>
      <TrackingSSG
        payload={{
          variant: defaultVariant,
          product,
          qty: 1,
          b,
          cartId,
        }}
      />
      <ProductSchema
        config={configSchema}
        productMeta={{
          name: ProductMeta?.title,
          description: ProductMeta?.description,
          sku: product?.sku,
          category: product?.categories?.[0]?.name,
          aggregateRating: aggregateRating,
          offers: offers,
          image: [
            {
              url: ProductMeta?.image,
              caption: ProductMeta?.title,
            },
          ],
        }}
      />

      <SEOTags {...ProductMeta}>
        <link rel="canonical" href={linkDefaultVariant} data-vm-ssr="true" />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="600" />
        <meta
          property="product:price:amount"
          content={
            defaultVariant?.product?.price_range?.minimum_price?.regular_price
              ?.value + "" || ""
          }
        />
        <meta
          property="product:price:currency"
          content={
            defaultVariant?.product?.price_range?.minimum_price?.regular_price
              ?.currency ||
            process.env.NEXT_PUBLIC_CURRENCY ||
            "USD"
          }
        />
        <meta property="og:availability" content="instock" />
      </SEOTags>
      <ReMaketing
        data={{
          price: product?.price_range?.minimum_price?.final_price?.value,
          sku: product?.sku,
        }}
        gtagType={REMARKETING_PRODUCT}
      />
      <>
        <BreadcrumbJsonLd
          itemListElements={breadcrumbData?.map((item, index) => {
            return {
              position: index + 1,
              name: item.title,
              item: `${process.env.NEXT_PUBLIC_URL}${item.path}`,
            };
          })}
        />
      </>
      <Layout
        isUseLayout
        className="lg:pt-3"
        dataMegaMenu={dataMegaMenu}
        storeConfig={storeConfig}
      >
        <Container className="lg:block hidden ">
          <Breadcrumb items={breadcrumbData} />
        </Container>
        <div className="flex flex-col gap-3 min-h-screen">
          {relatedByPosition?.[RelatedPosition.ProductContentTop] && (
            <RelatedTop
              data={relatedByPosition?.[RelatedPosition.ProductContentTop]}
              parentSlug={slug}
              sku={product?.sku}
              firstRelated={firstRelated}
              relatedTopProducts={relatedTopProducts}
              isMobile={isMobile}
            />
          )}
          {sections.map((section: any, index: number) => {
            const Section = section.component;
            return (
              <React.Fragment key={`section-${index}`}>
                {React.isValidElement(Section) ? (
                  React.cloneElement(Section as React.ReactElement, {
                    data: relatedRules?.getRelatedRules,
                    ...get(sectionData, section.dataKey, {}),
                    relatedTopProducts,
                  })
                ) : (
                  <Section
                    data={relatedRules?.getRelatedRules}
                    {...get(sectionData, section.dataKey, {})}
                    relatedTopProducts={relatedTopProducts}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {!isUsingLayoutV2 && (
          <Container className="block lg:hidden text-center pt-8">
            <Breadcrumb items={breadcrumbData} />
          </Container>
        )}
      </Layout>
    </Fragment>
  );
};

export default ProductDetail;

export const getServerSideProps: GetServerSideProps = async ({
  params,
  res,
  query,
  req,
}) => {
  try {
    const isMobile = useMobile(req.headers["user-agent"]);

    const apolloClient = initializeSSRApollo({
      httpLinkProps: { useGETForQueries: true },
    });
    let queryVariant = query?.variant || "";
    let faqData = null;
    const url_key = params?.slug;
    const b = Number(query?.b);
    const slug: string = (params?.slug as string) || "";
    const regex = /\b(\d{12})(-p)?\b/;
    const matching: any = slug.match(regex);
    const SKU = matching?.[1];

    const [
      dataRes,
      dataMegaMenuRes,
      responseRes,
      dataSchemaRes,
      blockDataRes,
      trackingDataRes,
    ] = await Promise.allSettled([
      apolloClient.query({
        query: QUERY_GET_META_PRODUCT_DETAIL_BY_SLUG,
        variables: {
          param: params?.slug,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: QUERY_GET_MEGA_MENU_GQL,
        variables: {
          identifiers: Identifiers.MainMenu,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: GET_STORE_CONFIG_ON_DOCUMENT,
      }),
      apolloClient.query({
        query: GET_SCHEMA_PRODUCT,
      }),
      apolloClient.query({
        query: QUERY_GET_CMS_BLOCKS,
        variables: { identifiers: SSRIdentifiers },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: GET_PRODUCTS_TRACKING,
        variables: { skus: SKU ? [SKU] : [] },
        context: { fetchOptions: { method: "GET" } },
      }),
    ]);

    const data = (dataRes as any)?.value?.data;
    const dataMegaMenu = (dataMegaMenuRes as any)?.value?.data;
    const response = (responseRes as any)?.value;
    const dataSchema = (dataSchemaRes as any)?.value?.data;
    const blockData = (blockDataRes as any)?.value?.data;
    const trackingData = (trackingDataRes as any)?.value?.data;

    if (data?.products?.items?.length === 0) {
      if (!!SKU) {
        const { data: dataSKU } = await apolloClient.query({
          query: QUERY_PRODUCT_DETAIL_BY_SKU,
          variables: {
            param: SKU,
          },
        });
        if (res && dataSKU?.products?.items?.length > 0) {
          const { products } = dataSKU;
          const correctURL = products?.items[0].url_key;
          const querystring = require("querystring");
          const tempQuery = { ...query };
          delete tempQuery["slug"];
          let newQuery = querystring.stringify(tempQuery);
          if (!!newQuery) {
            newQuery = "?" + newQuery;
          }
          res.writeHead(301, {
            Location: `${PATH.ProductDetail.replace("[slug]", correctURL)}${newQuery}`,
          });
          res.end();
        } else {
          return {
            notFound: true,
          };
        }
      } else {
        return {
          notFound: true,
        };
      }
    } else {
      const newProduct = data?.products?.items[0];
      const variants = get(newProduct, "variants", "");
      const isCorrectVariant = variants?.find(
        (item: any) => item?.product.id + "" === query?.variant
      );

      if (!isCorrectVariant) {
        queryVariant = get(newProduct, "variants[0].product.id", "");
        const querystring = require("querystring");
        const tempQuery = { ...query };
        tempQuery["variant"] = queryVariant;
        delete tempQuery["slug"];
        let newQuery = querystring.stringify(tempQuery);
        if (!!newQuery) {
          newQuery = "?" + newQuery;
        }
        return {
          redirect: {
            permanent: true,
            destination: `/products/${url_key}${newQuery}`,
          },
        };
      }
    }

    const storeConfig = get(response, "data.storeConfig", {
      frontend_config_logo: null,
      store_notice_enable: null,
      frequently_bought_together_group_id: null,
    });

    faqData = get(data, "products.items[0].productQuestions", "");

    const defaultVariant =
      data?.products?.items?.[0]?.variants.find(
        (item: ProductVariant) => item.product.id + "" === queryVariant
      ) || null;

    let relatedByPosition: any = {};
    let firstRelated = null;
    let relatedTopProducts = null;
    let relatedRules = null;
    const product = transformProductItem(data?.products?.items?.[0]);
    if (!!data?.products?.items?.[0]?.sku) {
      const { data: related } = await apolloClient.query({
        query: GET_RELATED_RULES,
        variables: {
          sku: data?.products?.items?.[0]?.sku,
        },
      });
      relatedRules = related;

      const dataTemp = related?.getRelatedRules || [];
      firstRelated =
        dataTemp?.filter((item: any) =>
          [
            RelatedPosition.ProductContentTop,
            RelatedPosition.ProductContentBottom,
          ].includes(item.block_position)
        )?.[0] || {};

      dataTemp?.map((item: any) => {
        if (!!relatedByPosition[item.block_position]) {
          relatedByPosition[item.block_position].push(item);
        } else {
          relatedByPosition[item.block_position] = [item];
        }
      });

      const products =
        process.env.NEXT_PUBLIC_TURN_OFF_SERVERSIDE_RELATED === "1"
          ? []
          : [
              ...(relatedByPosition?.[RelatedPosition.ProductContentTop] ?? []),
              ...(relatedByPosition?.[RelatedPosition.ProductContentBottom] ??
                []),
            ].map(async (item: any) => {
              const { data } = await apolloClient.query({
                query: QUERY_RELATED_PRODUCT,
                variables: {
                  sku: product.sku,
                  ruleId: item.group_id,
                  limit: item.max_products || 12,
                  skip:
                    !item.group_id ||
                    process.env.NEXT_PUBLIC_TURN_OFF_SERVERSIDE_RELATED === "1",
                },
                context: { fetchOptions: { method: "GET" } },
              });
              return data;
            });

      await Promise.allSettled(products).then((products) => {
        relatedTopProducts = products.map((product: any) => product.value);
      });
    }

    const serverCmsBlocks = blockData.cmsBlocks.items.reduce(
      (acc: any, cur: any) => {
        acc[cur.identifier] = cur;
        return acc;
      },
      {} as any
    );

    const pageProps = {
      props: {
        product,
        tracking: trackingData?.productsBySkus?.items?.[0] || null,
        slug: params?.slug,
        faqData,
        dataMeta: data?.products?.items?.[0] || {},
        defaultVariant,
        b,
        configSchema: dataSchema?.MarketingConfig?.schemaProduct || {},
        relatedByPosition,
        firstRelated,
        dataMegaMenu,
        storeConfig,
        relatedTopProducts,
        relatedRules,
        isMobile,
        serverCmsBlocks,
      },
    };
    // ssrCache.set(cacheKey, pageProps);
    // setTimeout(() => ssrCache.delete(cacheKey), 1000 * 60 * 5);

    return pageProps;
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }
  // const cacheKey = req?.url;
  // if (ssrCache.has(cacheKey)) {
  //   return ssrCache.get(cacheKey);
  // }
};
